/**
 * 1.3.0 "Assign for delivery…" (permission portal.assign): assign placeholders to a portal company or user, with a
 * delivery spec, a due date, notes and the requested items. Stream P2's: files under pages/assignments/ are P2's.
 *
 * Mounted by step 0 in the asset page's actions (a placeholder) and in the Library selection bar (a multi-selection;
 * only the placeholders are assigned, the dialog says how many it leaves out).
 *
 * On the asset page: without an assignment, "Assign for delivery…" opens the dialog; with one (open, or accepted),
 * the button shows its state and opens the Delivery panel (the items' states, Edit, Cancel). portal.review holders
 * see the panel without the actions.
 */
import { useState } from 'react';
import { Truck } from 'lucide-react';
import type { AssetDetail, AssetSummary } from '@prism/contracts';
import { useMe } from '../../api/hooks';
import { useActiveDeliveries, useAssetAssignment } from '../../api/hooks/assignments';
import { Modal } from '../../components/Modal';
import { AssignDialog } from './AssignDialog';
import { ASSIGNMENT_STATE_LABEL, DeliveryPanel } from './DeliveryPanel';

const READY = true;

function usePortalRights(): { assign: boolean; review: boolean } {
  const p = useMe().data?.permissions ?? [];
  return { assign: p.includes('portal.assign'), review: p.includes('portal.review') };
}

/** On a placeholder's page (not in the recycle bin); and on an asset whose delivery was accepted, its Delivery panel. */
export function AssignForDeliveryButton({ asset }: { asset: Pick<AssetDetail, 'id' | 'assetCode' | 'title' | 'status' | 'deleted'> }) {
  const can = usePortalRights();
  const [open, setOpen] = useState<'assign' | 'panel' | 'edit' | null>(null);
  const assignment = useAssetAssignment(asset.id, READY && (can.assign || can.review) && !asset.deleted).data;
  if (!READY || asset.deleted || !(can.assign || can.review)) return null;
  if (!assignment) {
    if (!can.assign || asset.status !== 'placeholder') return null;
    return (
      <>
        <button className="btn sm" data-testid="assign-delivery" title="Ask a production company to deliver this placeholder through the media portal" onClick={() => setOpen('assign')}>
          <Truck size={14} /> Assign for delivery…
        </button>
        {open === 'assign' && <AssignDialog targets={[asset]} onClose={() => setOpen(null)} />}
      </>
    );
  }
  const waiting = assignment.items.filter((i) => i.state === 'open' || i.state === 'rejected').length;
  return (
    <>
      <button className={`btn sm delivery-button ${assignment.overdue ? 'overdue' : ''}`} data-testid="delivery-button" onClick={() => setOpen('panel')}
        title={`${assignment.portalUser?.name ?? assignment.company.name}: ${waiting} of ${assignment.items.length} item${assignment.items.length === 1 ? '' : 's'} still to come`}>
        <Truck size={14} /> Delivery: {assignment.overdue ? 'overdue' : ASSIGNMENT_STATE_LABEL[assignment.state].toLowerCase()}
      </button>
      {open === 'panel' && (
        <Modal title={`Delivery of ${asset.assetCode}`} wide onClose={() => setOpen(null)}>
          <DeliveryPanel assignment={assignment} onClosed={() => setOpen(null)} onEdit={() => setOpen('edit')} />
        </Modal>
      )}
      {open === 'edit' && <AssignDialog existing={assignment} onClose={() => setOpen('panel')} />}
    </>
  );
}

/** In the Library selection bar: the selected placeholders. */
export function AssignForDeliverySelectionButton({ selected }: { selected: AssetSummary[] }) {
  const can = usePortalRights().assign;
  const [open, setOpen] = useState(false);
  const placeholders = selected.filter((a) => a.status === 'placeholder');
  if (!READY || !can) return null;
  return (
    <>
      <button className="btn ghost sm" disabled={!placeholders.length} data-testid="selection-assign-delivery" data-eligible={placeholders.length} onClick={() => setOpen(true)}
        title={placeholders.length ? 'Ask a production company to deliver these placeholders through the media portal' : 'Only placeholders can be assigned for delivery'}>
        <Truck size={13} /> Assign {placeholders.length} for delivery…
      </button>
      {open && <AssignDialog targets={placeholders} skipped={selected.length - placeholders.length} onClose={() => setOpen(false)} />}
    </>
  );
}

export { DeliveryPanel } from './DeliveryPanel';

/** A Library card's "Delivery: open / overdue" on a placeholder with an active assignment (portal.assign or portal.review). */
export function DeliveryBadge({ assetId }: { assetId: string }) {
  const can = usePortalRights();
  const active = useActiveDeliveries(READY && (can.assign || can.review)).data;
  if (!active?.has(assetId)) return null;
  const overdue = active.get(assetId);
  return (
    <span className={`pill delivery-badge ${overdue ? 'danger' : 'accent'}`} data-testid="delivery-badge" title="Assigned for delivery through the media portal">
      <Truck size={11} /> {overdue ? 'Delivery overdue' : 'Delivery open'}
    </span>
  );
}

/**
 * The Delivery panel inline, in a placeholder's empty player (the merge's mount): the assignment's state and items at
 * a glance, with Edit and Cancel for portal.assign holders. Nothing without an assignment or the portal rights.
 */
export function PlaceholderDelivery({ asset }: { asset: Pick<AssetDetail, 'id' | 'assetCode' | 'deleted'> }) {
  const can = usePortalRights();
  const [editing, setEditing] = useState(false);
  const assignment = useAssetAssignment(asset.id, READY && (can.assign || can.review) && !asset.deleted).data;
  if (!READY || asset.deleted || !assignment) return null;
  return (
    <div className="placeholder-delivery" data-testid="placeholder-delivery">
      <h3><Truck size={15} /> Delivery through the media portal</h3>
      <DeliveryPanel assignment={assignment} onEdit={() => setEditing(true)} />
      {editing && <AssignDialog existing={assignment} onClose={() => setEditing(false)} />}
    </div>
  );
}
